import { EScheduledBy, EScheduleStatusValue } from './calendar';
import { EContactTypeV2, EServiceType } from './common';
import { TextVisitServiceType } from './textVisit';

export type TConversationStatus = 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'CLOSED';

export enum EMessageType {
    TEXT = 'TEXT',
    EMAIL = 'EMAIL',
    APPOINTMENT = 'APPOINTMENT',
    AVA_REPLY = 'AVA_REPLY',
    QUESTION = 'QUESTION',
    TIME_LABEL = 'TIME_LABEL',
    UNREAD_MARK = 'UNREAD_MARK',
    TEXT_VISIT = 'TEXT_VISIT',
    REVIEW = 'REVIEW',
    FILE = 'FILE',
    KLARITY_SUPPORT = 'KLARITY_SUPPORT',
    NEW_TEXT_VISIT_PROVIDER_CREATED = 'NEW_TEXT_VISIT_PROVIDER_CREATED',
    RECURRING_REFILL_UPDATED = 'RECURRING_REFILL_UPDATED',
    // eligibility 保险卡两类 system 卡片（FRONTEND_INTEGRATION §9）：会话两侧各一条，
    // 前端只渲染 provider 侧 payload；patient 侧在 patient portal（不归本项目）
    INSURANCE_CARD_REQUEST = 'INSURANCE_CARD_REQUEST',
    INSURANCE_CARD_UPLOADED = 'INSURANCE_CARD_UPLOADED',
    TREATMENT_PLAN = 'TREATMENT_PLAN',
}

export enum EMessageSenderType {
    PATIENT = 'PATIENT',
    PROVIDER = 'PROVIDER',
    SYSTEM = 'SYSTEM',
    KLARITY_USER = 'KLARITY_USER',
    OPS = 'OPS',
    GP_USER = 'GP_USER',
    GP_PROSPECT_IDENTITY = 'GP_PROSPECT_IDENTITY',
}

export enum EWSAction {
    ERROR = 'ERROR',
    NEWMSG = 'NEWMSG',
}

export enum EConversationType {
    NORMAL = 'NORMAL',
    PROVIDER_REVIEW = 'PROVIDER_REVIEW',
    GP_STAFF_PATIENT = 'GP_STAFF_PATIENT',
    GP_INTERNAL = 'GP_INTERNAL',
}

export enum EGPParticipantType {
    GP_USER = 'GP_USER',
    GP_PROSPECT_IDENTITY = 'GP_PROSPECT_IDENTITY',
}

// {
//     "conversationId": "1446_106711",
//     "conversationType": "NORMAL",
//     "lastMessageId": "f9f301fae7e440caa94641896e03549d",
//     "lastMessageContent": "You’ve sent a visit notes to patient regarding their text visit for...",
//     "lastMessageType": "TEXT_VISIT",
//     "lastMessageAt": "2026-02-06T07:55:04Z",
//     "unreadCount": 2,
//     "status": "ACTIVE",
//     "lastReadAt": "2026-02-06T07:54:56Z",
//     "createdAt": "2025-12-18T02:18:26Z",
//     "members": [
//         {
//             "userId": 1446,
//             "userType": "PROVIDER",
//             "firstName": "Xiaocong",
//             "lastName": "Liu",
//             "fullName": "Xiaocong Liu",
//             "avatar": "https://saas-dev-jp.s3.ap-northeast-1.amazonaws.com/dev/7fdfcdfc-8dd1-414c-88db-a7d7d44ecc13.png",
//             "isOnline": false
//         },
//         {
//             "userId": 106711,
//             "userType": "PATIENT",
//             "firstName": "patient evan",
//             "lastName": "patient Liu",
//             "fullName": "patient evan patient Liu",
//             "avatar": null,
//             "isOnline": false
//         }
//     ],
//     "memberCount": 2,
//     "totalMessageCount": 155
// }

export type TConversationMember = {
    userId: number;
    userType: EMessageSenderType;
    firstName: string;
    lastName: string;
    fullName: string;
    avatar: string | null;
    isOnline: boolean;
};

export type TConversation = {
    conversationId: string;
    conversationType?: EConversationType | string; // 对话类型，PROVIDER_REVIEW 表示评价相关对话
    lastMessageId: string;
    lastMessageContent: string;
    lastMessageAt: string;
    lastMessageType: EMessageType;
    lastMessageSenderType: EMessageSenderType;
    unreadCount: number;
    status: TConversationStatus;
    lastReadAt: string | null;
    createdAt: string;
    memberCount: number;
    totalMessageCount: number;
    members: TConversationMember[];
    /**
     * OPS conversations may include the related provider metadata.
     * - 'E' => PPV
     * - 'K' => PPA
     */
    providerId?: number | null;
    /**
     * Backward compatible:
     * - historical: 'E' | 'K' | null
     * - new possible format: 'E,K' (comma-separated), or other server strings
     */
    providerType?: string | null;
    clientId?: number | null;
    isOpsByMe?: boolean;
    // patientFirstName: string | null;
    // patientLastName: string | null;
    // providerFirstName: string;
    // providerLastName: string;
    // providerAvatarUrl: string | null;
    // providerOnline?: boolean;
    // patientOnline?: boolean;
};

export type TServerMessage = {
    messageId: string; //"10fb8578014c47e8aa6d6aff149f7ebd",
    conversationId: string; //"1417_106428",
    senderId: number; //106428,
    senderType: EMessageSenderType;
    receiverId: number; //106428,
    receiverType: EMessageSenderType;
    messageType: EMessageType;
    replyToMessageId?: string; //,
    visibleTo: EMessageSenderType[];
    createdAt: string; // "2025-05-27T07:53:58.952Z",
    updatedAt: string; //"2025-05-27T07:53:58.952Z",
    sortKey: string; //"2025-05-27T07:53:58.952Z#uqVYXS"

    content: string; // "{\"body\":\"Thank you for reaching out. I am Evan, your healthcare provider. How can I assist you today?\"}",
    aiReplyContent?: string;
};

export type TMessageText = {
    body: string;
};

export type TQuestion = {
    subject: string;
    body: string;
};

export enum EAppointmentType {
    RESCHEDULED = 'RESCHEDULED',
    PATIENT_CANCELED = 'PATIENT_CANCELED',
    PATIENT_LATE_CANCELED = 'PATIENT_LATE_CANCELED',
    PROVIDER_CANCELED = 'PROVIDER_CANCELED',
    NEW_APPOINTMENT_CREATED = 'NEW_APPOINTMENT_CREATED',
    TREATMENT_PLAN_SUBMITTED = 'TREATMENT_PLAN_SUBMITTED',
    TREATMENT_PLAN_ADDENDUM_CREATED = 'TREATMENT_PLAN_ADDENDUM_CREATED',
    VISIT_REASSIGNED_PATIENT = 'VISIT_REASSIGNED_PATIENT',
    VISIT_REASSIGNED_NEW_PROVIDER = 'VISIT_REASSIGNED_NEW_PROVIDER',
    VISIT_REASSIGNED_OLD_PROVIDER = 'VISIT_REASSIGNED_OLD_PROVIDER',
}

export type TAppointment = {
    type: EAppointmentType;
    id: number;
    appointmentName: string; //"Infections: Prescription service",
    providerId: number;
    clientId: number;
    startAt: string; //"2025-05-30T03:25",
    duration: number;
    contactType: EContactTypeV2;
    appointmentStatus: EScheduleStatusValue; //"scheduled",
    notes: string;
    reason: string;
    scheduledBy: EScheduledBy;
    serviceType: EServiceType;
    serviceSpecialty: string; //"Infections",
    serviceState: string; // "CA",
    businessId?: number;
    createdAt: string;

    oldStartAt: string;
    oldDuration?: number;
    chargeFee?: number;
    oldProviderName?: string;
    newProviderName?: string;
    patientName?: string;
    meetingLink?: string;
    intakeFormsLink?: string;
    visitNotesLink?: string;
};

export type TEmail = {
    subject: string;
    sender: string;
    recevier: string;
    body: string;
    providerEmailId: number;
};

export type TAvaReply = {
    body: string;
    llmMessageId: string;
    feedBack?: 'like' | 'dislike' | null; // 空代表没有回复过
};

export type TReview = {
    id: number;
    uuid: string;
    bpProviderId: number;
    providerSource: string;
    commentId: number;
    rating: number; // 评分，1-5
    tags?: string; // 标签，逗号分隔，如 "Professional,Compassionate,Empathetic"
    source: string; // 来源，如 "EHR_BOOKING"
    sourceId: string;
    contentLength: number;
    repliedAt?: string | null; // 回复时间
    isReplied?: boolean; // 是否已回复
    isNeedReplied?: boolean; // 是否需要回复
    repliedContent?: string; // 回复内容
    reviewStatus: string; // 评价状态，如 "S"
    submitAt: string; // 提交时间
    reviewerName?: string;
    reviewerEmail?: string;
    reviewerPhone?: string;
    reviewComment?: string; // 评价内容
    appointmentName?: string; // 预约名称
    appointmentSpecialty?: string; // 预约专业
    appointmentServiceType?: string; // 预约服务类型
    appointmentStartAt?: string; // 预约开始时间
    appointmentDuration?: number; // 预约时长（分钟）
    patientFirstName?: string;
    patientLastName?: string;
    isPublic?: boolean; // 是否为公开评论
};

export type TFile = {
    fileId: number;
    fileName: string;
    fileOriginName: string;
    fileExtension: string;
    fileUri: string;
    fileType: string;
    fileSize: number;
};

export enum ETextVisitType {
    NEW_TEXT_VISIT_CREATED = 'NEW_TEXT_VISIT_CREATED',
    NEW_FOLLOW_UP_MESSAGE_CREATED = 'NEW_FOLLOW_UP_MESSAGE_CREATED',
    TEXT_VISIT_TREATMENT_PLAN_SUBMITTED = 'TEXT_VISIT_TREATMENT_PLAN_SUBMITTED',
    NEW_TEXT_VISIT_PROVIDER_CREATED = 'NEW_TEXT_VISIT_PROVIDER_CREATED',
    TREATMENT_PLAN_ADDENDUM_CREATED = 'TREATMENT_PLAN_ADDENDUM_CREATED',
    // Hotfix 2026-05-12: prescription refill payment hold
    TEXT_VISIT_PAYMENT_HOLD = 'TEXT_VISIT_PAYMENT_HOLD',
}

export enum ETextVisitSenderType {
    KLARITY_USER = 'KLARITY_USER',
    PROVIDER = 'PROVIDER',
}

export const ETextVisitConditionMap = {
    [TextVisitServiceType.INITIAL_VISIT]: 'conditions',
    [TextVisitServiceType.MEDICATION_REFILL]: 'medication refill',
    [TextVisitServiceType.MEDICATION_CHANGE]: 'medication change',
    [TextVisitServiceType.FOLLOW_UP_CONSULTATION]: 'follow-up consultation',
    [TextVisitServiceType.OTHER]: 'other care needs',
    [TextVisitServiceType.PRESCRIPTION_REFILL]: 'prescription refill',
};

export type TTextVisit = {
    type: ETextVisitType;
    id: number;
    assignedProviderId: number;
    assignedProviderAt: string;
    clientId: number;
    textVisitUuid: string;
    billingId: number;
    status: string;
    conditions: string;
    createdAt: string;
    serviceType: TextVisitServiceType;
    assessmentName?: string; // 评估名称，优先显示
    medications?: string; // "[\"adhd drug\"]"
    latestMessage?: {
        // 只有 type 是 NEW_FOLLOW_UP_MESSAGE_CREATED 时才有这个字段
        id: number;
        textVisitId: number;
        senderType: ETextVisitSenderType;
        senderId: number;
        message: string;
        createdAt: string;
    };
    businessIdOrUuid?: string;
};

//{\"type\":\"KLARITY_SUPPORT_SUBMIT\",\"id\":26,\"category\":\"BILLING_AND_PAYMENTS\",\"subCategory\":\"REFUND_REQUEST\",\"relatedChargeId\":1054,\"chargeType\":\"LATE_CANCELLATION\",\"amountCents\":80,\"billingFrom\":\"PROVIDER_APPOINTMENT_BILLING\",\"chargeToType\":\"PROVIDER\",\"chargeToId\":1331,\"chargeToName\":\"sad ad\",\"note\":\"test other billing questions\"}

export enum ECategory {
    BILLING_AND_PAYMENTS = 'BILLING_AND_PAYMENTS',
    OTHER_QUESTIONS = 'OTHER_QUESTIONS',
}
// REFUND_REQUEST,CHARGE_REQUEST,OTHER_BILLING_QUESTIONS
export enum ESubCategory {
    REFUND_REQUEST = 'REFUND_REQUEST',
    CHARGE_REQUEST = 'CHARGE_REQUEST',
    OTHER_BILLING_QUESTIONS = 'OTHER_BILLING_QUESTIONS',
}
export type TSupportMessage = {
    type: string;
    id: number;
    category: ECategory;
    subCategory: ESubCategory;
    relatedChargeId: number;
    chargeType: string;
    amountCents: number;
    billingFrom: string;
    chargeToType: string;
    chargeToId: number;
    chargeToName: string;
    note: string;
};
//{\"type\":\"KLARITY_SUPPORT_TRANSFER\",\"body\":{\"newOpsName\":\"zhengkai zou\",\"currentOpsName\":\"xiaocong liu\"}}
export type TTransferMessage = {
    type: string;
    body: {
        newOpsName: string;
        currentOpsName: string;
    };
};

//"{\"type\":\"KLARITY_SUPPORT_CLOSE\",\"body\":{\"patientName\":\"Evan Liu Liu\"}}"
export type TCloseMessage = {
    type: string;
    body: {
        patientName: string;
    };
};

export type TRecurringRefillUpdated = {
    type: string;
    seriesId: number;
    providerId: number;
    clientId: number;
    providerName?: string;
    specialtyName?: string;
    serviceType?: string;
    originalRefillDate?: string;
    originalRefillDateFormatted?: string;
    newRefillDate?: string;
    newRefillDateFormatted?: string;
    originalRecurrenceType?: string;
    newRecurrenceType?: string;
    originalRecurrenceDesc?: string;
    newRecurrenceDesc?: string;
};

export type TMessage = {
    messageId: string; //"10fb8578014c47e8aa6d6aff149f7ebd",
    conversationId: string; //"1417_106428",
    senderId: number; //106428,
    senderType: EMessageSenderType;
    receiverId: number; //106428,
    receiverType: EMessageSenderType;
    replyToMessageId?: string; //,
    visibleTo: EMessageSenderType[];
    createdAt: string; // "2025-05-27T07:53:58.952Z",
    updatedAt: string; //"2025-05-27T07:53:58.952Z",
    sortKey: string; //"2025-05-27T07:53:58.952Z#uqVYXS"

    showSendTime?: boolean; // 是否显示发送时间
    messageType: EMessageType;
    text?: TMessageText;
    appointment?: TAppointment;
    question?: TQuestion;
    textVisit?: TTextVisit;

    email?: TEmail;
    avaReply?: TAvaReply;
    review?: TReview;
    timeLabel?: string;
    file?: TFile;
    supportMessage?: TSupportMessage;
    transferMessage?: TTransferMessage;
    closeMessage?: TCloseMessage;
    recurringRefillUpdated?: TRecurringRefillUpdated;
    insuranceCardRequest?: TInsuranceCardRequest;
    insuranceCardUploaded?: TInsuranceCardUploaded;
    treatmentPlanMessage?: TTreatmentPlanMessage;
};

// INSURANCE_CARD_REQUEST 的 provider 侧 payload（§9.2）。文案按消息类别静态渲染（已拍板不判状态）；
// Send reminder / 操作人文案待产品拍板后补
export type TInsuranceCardRequest = {
    clientId?: number;
    patientFirstName?: string;
    // provider 在 card request 弹窗写的备注；可能为 null
    messageToPatient?: string | null;
    createdAt?: string;
};

// INSURANCE_CARD_UPLOADED 两侧同构 payload（§9.3）
export type TInsuranceCardUploaded = {
    providerId?: number;
    clientId?: number;
    createdAt?: string;
};

export type TTreatmentPlanMessage = {
    type: 'TREATMENT_PLAN_SUBMITTED' | 'TREATMENT_PLAN_ADDENDUM_CREATED';
    businessType: 'STANDALONE';
    businessId: number;
    businessIdOrUuid: string;
};

export type TWSMessage = {
    action: EWSAction;
    uuid: string; // "87aebd9e27134e02b1d35d6e9a73ee37",
    payload?: TServerMessage;
};

export type TGetProviderAppointmentListInput = {
    current: number;
    size: number;
    type: 'Past' | 'Upcoming';
    clientId?: number;
};

export type TMessageFrom = {
    name: string;
    value: TMessage;
};

export interface IFileUploadResult extends TFile {
    id: number;
}

// GP (Group Practice) types

export type TGPConversation = TConversation & {
    conversationType: EConversationType.GP_STAFF_PATIENT | EConversationType.GP_INTERNAL | EConversationType;
    identityId?: number;
    organizationId?: number;
};

export type TGPRecipientPatient = {
    clientId: number;
    firstName: string;
    lastName: string;
    identityId: number;
    providerName?: string;
    providerId?: number;
    email?: string;
};

export type TGPSendMessageParams = {
    conversationId?: string;
    recipientType?: string;
    targetId?: number;
    message?: string;
    fileId?: number;
};

export type TGPGetMessagesParams = {
    conversationId: string;
    size?: number;
    cursor?: string;
    identityId?: number;
};

export type TGPScope = 'my' | 'all';
